import { useEffect } from "react";

/**
 * Focus Trap Hook für Modals
 * Begrenzt die Tab-Navigation auf fokussierbare Elemente innerhalb des Modals
 * und stellt den vorherigen Focus wieder her, wenn das Modal geschlossen wird
 */
export function useFocusTrap(isActive, modalRef) {
  useEffect(() => {
    if (!isActive || !modalRef?.current) return;

    const previousActiveElement = document.activeElement;
    
    const getFocusableElements = () => {
      const focusableSelectors = [
        "button:not(:disabled)",
        "[href]",
        "input:not(:disabled)",
        "select:not(:disabled)",
        "textarea:not(:disabled)",
        "[tabindex]:not([tabindex='-1'])"
      ].join(",");

      return Array.from(
        modalRef.current.querySelectorAll(focusableSelectors)
      );
    };

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      // Shift + Tab auf erstem Element -> zum letzten springen
      if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab auf letztem Element -> zum ersten springen
      else if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Initial Focus auf erstes fokussierbares Element im Modal setzen
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    // Event Listener
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Focus zurückgeben an vorheriges Element (z.B. Button, der Modal öffnete)
      if (previousActiveElement && typeof previousActiveElement.focus === "function") {
        previousActiveElement.focus();
      }
    };
  }, [isActive, modalRef]);
}
